/// <reference types="@types/wicg-file-system-access" />
import _ from 'lodash';
import * as log from '@/gr/common/log';
import { TrendDataDefinition, TrendDataRepository, TrendData, Messages, FileNamingService, DateTimeFormatter } from '@/apps/timeSeriesViewer';
import * as json2csv from 'json2csv';
import * as os from 'os';
import ko from 'knockout';
import fileSaver from 'file-saver';

export class CsvDownloadService {
  private writer: WritableStreamDefaultWriter | undefined = undefined;
  private trendData = ko.observable<TrendData | null>(null);
  private firstChunk = true;
  constructor(
    private readonly _trendDataRepository: TrendDataRepository,
    private readonly _fileNamingService: FileNamingService
  ) {}

  async downloadAsCsv(dataDefinition: TrendDataDefinition, messages: Messages.Args, progress: KnockoutObservable<string>, isLoading: KnockoutObservable<boolean>): Promise<void> {
    if (typeof window.showSaveFilePicker !== 'function') return this.fallbackDownloadAsCsv(dataDefinition, messages, progress, isLoading);

    try {
      const filename = `${this._fileNamingService.getDefaultFileName(this.trendData())}.csv`;
      const newHandle = await window.showSaveFilePicker({
        types: [
          {
            accept: {
              'text/csv': '.csv'
            }
          }
        ],
        startIn: 'downloads',
        suggestedName: filename
      });
      const writableStream = await newHandle.createWritable();
      this.writer = writableStream?.getWriter();
      this.firstChunk = true;
      await this._trendDataRepository.fillTrendData(dataDefinition, 'FILE', this.trendData, progress, isLoading, this);
    } catch (error) {
      if (error instanceof DOMException && error.code === DOMException.SECURITY_ERR) {
        console.warn('Falling back to old fileSaver');
        return this.fallbackDownloadAsCsv(dataDefinition, messages, progress, isLoading);
      }
      if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
        console.warn('User aborted request');
      } else {
        messages.add('Failed to request data for csv file');
        messages.add(error);
        log.errorEx(error, 'Failed to request data for csv file');
      }
      await this.writer?.abort();
      this.writer = undefined;
    } finally {
      await this.writer?.close();
    }
  }

  async fallbackDownloadAsCsv(dataDefinition: TrendDataDefinition, messages: Messages.Args, progress: KnockoutObservable<string>, isLoading: KnockoutObservable<boolean>): Promise<void> {
    try {
      const trendData = ko.observable<TrendData | null>(null);
      await this._trendDataRepository.fillTrendData(dataDefinition, 'FILE', trendData, progress, isLoading);

      const data = trendData();
      if (data) {
        data?.messages.forEach((message) => messages.add(message.html, message.type));
        const csv = this.convertToCsv(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const filename = `${this._fileNamingService.getDefaultFileName(this.trendData())}.csv`;
        fileSaver.saveAs(blob, filename);
      } else messages.add('No data to save');
    } catch (error) {
      messages.add('Failed to load data for csv file');
      log.errorEx(error, 'Failed to load data for csv file');
    }
  }

  public async writeChunk() {
    const data = this.trendData();
    if (data) {
      await this.saveAsCsv(data);
      this.firstChunk = false;
    }
  }

  private async saveAsCsv(trendData: TrendData) {
    const csv = this.convertToCsv(trendData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    await this.writer?.write(blob);
    await this.writer?.write(os.EOL);
  }

  private convertToCsv(trendData: TrendData) {
    // Format time stamps
    const data = trendData.dataPoints.map((dataPoints) => {
      const newDataPoints = _.clone(dataPoints);
      newDataPoints.timeStamp = DateTimeFormatter.dateTimeForExcel(newDataPoints.timeStamp, trendData.utcOffset);
      return newDataPoints;
    });

    const fields = [{ label: `Time (${trendData.utcOffset.displayName})`, value: 'timeStamp' }].concat(
      trendData.configuredDataDefinitions.map((c) => {
        const formattedUnits = c.unitsOfMeasure && c.unitsOfMeasure.displayName.length > 0 ? ` (${c.unitsOfMeasure.displayName})` : '';
        return { label: c.displayNameOrDefault() + formattedUnits, value: c.id };
      })
    );

    return json2csv.parse(data, { fields: fields, header: this.firstChunk });
  }
}
